import axios from 'axios';
import type { TFunction } from 'i18next';

/**
 * Session transport.
 *
 * The session lives in an HttpOnly cookie that JavaScript cannot read, so a
 * successful XSS can no longer walk off with the credential. What the browser
 * sends automatically it also sends on a forged cross-site request, so every
 * unsafe verb carries a double-submit CSRF token: the server sets a readable
 * `omaraty.csrf` cookie, we echo it back in `X-CSRF-Token`, and the server
 * rejects the request unless the two match.
 *
 * `USER_KEY` still holds *display* state (name, roles, active building) so the
 * shell can render before `/auth/me` resolves. It is not a credential, it is a
 * cache, and it is re-validated against the server on every load.
 */
export const USER_KEY = 'omaraty.user';
export const CSRF_COOKIE = 'omaraty.csrf';
export const CSRF_HEADER = 'X-CSRF-Token';

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  headers: { 'Content-Type': 'application/json' },
  // Same-origin deployment (nginx serves the SPA and proxies /api), so the
  // session cookie rides along without any CORS credential dance.
  withCredentials: true,
});

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

http.interceptors.request.use((config) => {
  if (UNSAFE_METHODS.has((config.method ?? 'get').toLowerCase())) {
    const token = readCookie(CSRF_COOKIE);
    if (token) config.headers.set(CSRF_HEADER, token);
  }
  return config;
});

/** RFC 7807 ProblemDetails, extended with i18n-ready error codes. */
export type ProblemDetails = {
  title?: string;
  status?: number;
  detail?: string;
  errorCodes?: string[];
  errorType?: string;
  errorId?: string;
  errorHint?: string;
  sqlState?: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly errorCodes: string[];
  /** #110: ما يقوله الخادم فعلاً عند 500 — نوع الخطأ وسطره الأول. */
  readonly detail: string;

  constructor(status: number, message?: string, errorCodes: string[] = [], detail = '') {
    super(message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.errorCodes = errorCodes;
    this.detail = detail;
  }

  /**
   * The request never reached the server: no network, DNS failure, or the
   * API is down. Distinct from a 4xx/5xx, where the server *did* answer —
   * and the distinction matters, because one is retryable and the other
   * usually is not.
   */
  get isConnectionError(): boolean {
    return this.status === 0;
  }
}

/** Set by ConnectionProvider so one failed request can raise the banner. */
let onConnectionChange: ((state: 'online' | 'offline' | 'serverDown') => void) | null = null;

export function setConnectionHandler(
  handler: ((state: 'online' | 'offline' | 'serverDown') => void) | null,
): void {
  onConnectionChange = handler;
}

export function reportConnection(state: 'online' | 'offline' | 'serverDown'): void {
  onConnectionChange?.(state);
}

/** Set by AuthProvider so a 401 anywhere drops the session everywhere. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

http.interceptors.response.use(
  (response) => {
    reportConnection('online');
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      const problem = error.response?.data as ProblemDetails | undefined;

      if (status === 0) {
        // No response at all. `navigator.onLine` separates "the device has no
        // network" from "the device is fine but our API is unreachable" —
        // two different sentences for the user, and only one of them is our
        // fault. It reports false negatives on some networks, never a false
        // "offline" when a request actually succeeded, so it is safe here.
        reportConnection(
          typeof navigator !== 'undefined' && navigator.onLine === false
            ? 'offline'
            : 'serverDown',
        );
        throw new ApiError(0, 'Network unreachable', ['errors.connectionLost']);
      }

      // Any answer at all means the connection is alive.
      reportConnection('online');

      // A refresh loop is worse than a login prompt: clear once, let the guard redirect.
      if (status === 401) onUnauthorized?.();
      // #130: رمز الخطأ دائماً (يُطابق سطر لوغ الخادم)؛ والتفصيل إن سمح الخادم.
      const detail = [
        problem?.errorId ? `#${problem.errorId}` : '',
        problem?.errorType, problem?.errorHint,
        problem?.sqlState ? `SQLSTATE ${problem.sqlState}` : '', problem?.detail,
      ].filter(Boolean).join(' — ');
      throw new ApiError(status, problem?.title ?? error.message, problem?.errorCodes ?? [], detail);
    }
    throw error;
  },
);

export function describeApiError(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('errors.forbidden');
    // #110: التفصيل من الخادم يُلحق دائماً — للـ500 وللأخطاء المعالَجة التي تحمله.
    const suffix = error.detail ? `\n${error.detail}` : '';
    if (error.errorCodes.length > 0) {
      return error.errorCodes.map((code) => t(code, { defaultValue: code })).join('\n') + suffix;
    }
    if (error.status === 401) return t('login.invalid');
    if (error.status === 429) return t('errors.tooManyAttempts');
    if (error.isConnectionError) return t('errors.connectionLost');
    return t('errors.unexpected') + suffix;
  }
  return t('errors.unexpected');
}

/** Simulated network latency for the mock transport. */
export function delay<T>(value: T, ms = 260): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}
